import { DOWNTIMEMETHODCAPTURE, NoDivisionEnum, USERTYPE } from "constants/constants";
import * as Yup from "yup";
import moment from "moment";
import { emailRegEx, passwordValidatorRegex } from "./matchers";

export const loginSchema = Yup.object().shape({
    email: Yup.string().matches(emailRegEx, "field_errors.invalid_email").required("field_errors.required_field"),
    password: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    showCode: Yup.boolean(),
    code: Yup.string().when("showCode", {
        is: true,
        then: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    }),
});

export const corporationFormSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    address1: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    address2: Yup.string(),
    country_id: Yup.number().required("field_errors.required_field"),
    phoneNumber: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    email: Yup.string().test("checkEmail", "", function (value) {
        const { path, createError } = this;
        const { dont_add_user, firstName, lastName } = this.parent;
        if (!dont_add_user || Boolean(firstName) || Boolean(lastName) || Boolean(value)) {
            if (!Boolean(value)) return createError({ path, message: "field_errors.required_field" });
            else if (!emailRegEx.test(value || "")) return createError({ path, message: "field_errors.invalid_email" });
            else return true;
        }
        return true;
    }),
    firstName: Yup.string().test("checkFirstName", "", function (value) {
        const { path, createError } = this;
        const { dont_add_user, email, lastName } = this.parent;
        if (!dont_add_user || Boolean(email) || Boolean(lastName) || Boolean(value)) {
            if (!Boolean(value)) return createError({ path, message: "field_errors.required_field" });
            else return true;
        }
        return true;
    }),
    lastName: Yup.string().test("checkLastName", "", function (value) {
        const { path, createError } = this;
        const { dont_add_user, email, firstName } = this.parent;
        if (!dont_add_user || Boolean(email) || Boolean(firstName) || Boolean(value)) {
            if (!Boolean(value)) return createError({ path, message: "field_errors.required_field" });
            else return true;
        }
        return true;
    }),
    state_name: Yup.string().required("field_errors.required_field"),
    city_name: Yup.string().required("field_errors.required_field"),
    zipCode: Yup.string().required("field_errors.required_field"),
    divisionInput: Yup.string(),
    divisions: Yup.array().of(
        Yup.object().shape({
            name: Yup.string(),
            deleted_at: Yup.string(),
            regions: Yup.array()
                .of(Yup.object().shape({ name: Yup.string(), deleted_at: Yup.string() }))
                .compact((region) => region.deleted_at)
                .min(1, "field_errors.at_least_one_item_required"),
        }),
    ),
});

export const millFormOsornoBackofficeSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    code: Yup.string(),
    division_id: Yup.string(),
    region_id: Yup.number().when("division_id", {
        is: (val: number | NoDivisionEnum.NO_DIVISION) => val !== NoDivisionEnum.NO_DIVISION,
        then: Yup.number().required("field_errors.required_field"),
    }),
    physical_address: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    billing_address: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    country_id: Yup.number(),
    phone_number: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    physical_zip_code: Yup.string()
        .trim()
        .min(1, "field_errors.required_field")
        .required("field_errors.required_field"),
    billing_zip_code: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    url: Yup.string().required("field_errors.required_field"),
    naics_code: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    email: Yup.string().when("dont_add_new_mill_manager", {
        is: false,
        then: Yup.string().matches(emailRegEx, "field_errors.invalid_email").required("field_errors.required_field"),
    }),
    first_name: Yup.string().when("dont_add_new_mill_manager", {
        is: false,
        then: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    }),
    last_name: Yup.string().when("dont_add_new_mill_manager", {
        is: false,
        then: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    }),
});

export const millSettingsStepFormSchema = Yup.object().shape({
    languageId: Yup.number().required("field_errors.required_field"),
    firstDayOfReportWeekId: Yup.number().required("field_errors.required_field"),
    firstDayFiscalYear: Yup.string().required("field_errors.required_field"),
    measureMetricId: Yup.number().required("field_errors.required_field"),
    timezone: Yup.string().required("field_errors.required_field"),
    allShiftsSoftDeleted: Yup.boolean(),
    shifts: Yup.array().when("allShiftsSoftDeleted", {
        is: false,
        then: Yup.array()
            .of(
                Yup.object().shape({
                    name: Yup.string(),
                    shift_historic: Yup.object().shape({
                        start: Yup.string().required("field_errors.required_field"),
                        end: Yup.string().required("field_errors.required_field"),
                        future_effective_at: Yup.date().test(
                            "is-now-or-tomorrow-or-later",
                            "fields_errors.minimum_effective_date_error",
                            function (value) {
                                const today = moment().startOf("day");
                                const tomorrow = moment().add(1, "day").startOf("day");
                                const selectedDate = moment(value).startOf("day");
                                const { shift_id } = this.parent;
                                return value
                                    ? !shift_id
                                        ? selectedDate.isSameOrAfter(today)
                                        : selectedDate.isSameOrAfter(tomorrow)
                                    : true;
                            },
                        ),
                        deleted_at: Yup.date(),
                        days: Yup.array().when("deleted_at", {
                            is: undefined,
                            then: Yup.array().of(Yup.number()).min(1, "field_errors.at_least_one_item_required"),
                            otherwise: Yup.array().nullable(),
                        }),
                    }),
                }),
            )
            .min(1, "field_errors.at_least_one_item_required"),
        otherwise: Yup.array()
            .of(
                Yup.object().shape({
                    name: Yup.string(),
                    shift_historic: Yup.object().shape({
                        start: Yup.string().required("field_errors.required_field"),
                        end: Yup.string().required("field_errors.required_field"),
                        effective_at: Yup.date(),
                        deleted_at: Yup.date(),
                        days: Yup.array().when("deleted_at", {
                            is: undefined,
                            then: Yup.array().of(Yup.number()).min(1, "field_errors.at_least_one_item_required"),
                            otherwise: Yup.array().nullable(),
                        }),
                    }),
                }),
            )
            .compact((shift) => shift.deleted_at)
            .min(1, "field_errors.at_least_one_item_required"),
    }),
});

export const generalInformationStepFormSchema = Yup.object().shape({
    holdingCompany: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    holdingCompanyAddress: Yup.string()
        .trim()
        .min(1, "field_errors.required_field")
        .required("field_errors.required_field"),
    holdingCompanyWebsite: Yup.string()
        .trim()
        .min(1, "field_errors.required_field")
        .required("field_errors.required_field"),
    corporateName: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    corporateAddress: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    corporateWebsite: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    corporatePhone: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    divisionInput: Yup.string(),
    divisions: Yup.array().of(
        Yup.object().shape({
            name: Yup.string(),
            deleted_at: Yup.string(),
            regions: Yup.array()
                .of(Yup.object().shape({ name: Yup.string(), deleted_at: Yup.string() }))
                .compact((region) => region.deleted_at)
                .min(1, "field_errors.at_least_one_item_required"),
        }),
    ),
});

export const productionLineCorporateBOFormSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    code: Yup.string(),
    shiftsIds: Yup.array().min(1, "field_errors.at_least_one_item_required"),
});

export const shiftDeletionFormSchema = Yup.object().shape({
    productionLineShiftIds: Yup.array().of(
        Yup.object().shape({
            productionLine: Yup.object().shape({
                id: Yup.number().required("field_errors.required_field"),
                name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
            }),
            shiftIds: Yup.array().of(Yup.number()).min(1, "field_errors.at_least_one_item_required"),
        }),
    ),
});

export const machineCenterCorporateBOFormSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    short_name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    manufacturer: Yup.string(),
    model_number: Yup.string(),
    is_duplication_with_selection: Yup.boolean(),
    is_duplication_without_selection: Yup.boolean(),
    production_line_id_send_duplicate: Yup.number().when("is_duplication_with_selection", {
        is: true,
        then: Yup.number().required("field_errors.required_field"),
    }),
    production_line_id_to_duplicate: Yup.number().when("is_duplication_without_selection", {
        is: true,
        then: Yup.number().required("field_errors.required_field"),
    }),
    machine_center_id_to_duplicate: Yup.number().when("is_duplication_without_selection", {
        is: true,
        then: Yup.number().required("field_errors.required_field"),
    }),
    downtime_method_capture_id: Yup.number().required("field_errors.required_field"),
    plc_address: Yup.string().when("downtime_method_capture_id", {
        is: DOWNTIMEMETHODCAPTURE.PLC,
        then: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    }),
    minimum_downtime: Yup.number().test(
        "greaterThan",
        "corporate_backoffice.production_line_view.machine_center_creation_form.minimum_downtime_definition_error",
        function (value) {
            const downtimeTrigger =
                this.parent.downtime_trigger_threshold || this.parent.downtime_trigger_threshold === 0
                    ? this.parent.downtime_trigger_threshold
                    : 60;
            return value || value === 0 ? value < downtimeTrigger : true;
        },
    ),
    downtime_trigger_threshold: Yup.number().test(
        "lessThan",
        "corporate_backoffice.production_line_view.machine_center_creation_form.downtime_trigger_error",
        function (value) {
            const minimumDowntime =
                this.parent.minimum_downtime || this.parent.minimum_downtime === 0 ? this.parent.minimum_downtime : 5;
            return value || value === 0 ? value > minimumDowntime : true;
        },
    ),
    is_bottleneck: Yup.boolean().required("field_errors.required_field"),
});

export const areaFormValidationSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    machine_center_id: Yup.number().required("field_errors.required_field"),
});

export const functionalLocationFormValidationSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    area_id: Yup.number().required("field_errors.required_field"),
});

export const failureTypeFormValidationSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
});

export const failureCodeFormValidationSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
});

export const addUserFormValidationSchema = Yup.object().shape({
    email: Yup.string().when("userTypeId", {
        is: USERTYPE.OPERATOR,
        then: Yup.string().notRequired(),
        otherwise: Yup.string()
            .matches(emailRegEx, "field_errors.invalid_email")
            .required("field_errors.required_field"),
    }),
    firstname: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    lastname: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    userTypeId: Yup.number().required("field_errors.required_field"),
});

export const deleteUserValidationSchema = Yup.object().shape({
    password: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
});

export const addRCAIssueFormValidationSchema = Yup.object().shape({
    shortDescription: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    description: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    isAdmin: Yup.boolean(),
    incidentDate: Yup.date().required("field_errors.required_field"),
    meetingTypeId: Yup.number(),
    millId: Yup.string().when("isAdmin", {
        is: true,
        then: Yup.string().required("field_errors.required_field"),
    }),
});

export const addObservationFormValidationSchema = Yup.object().shape({
    description: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
});

export const addActionPlanCommentFormValidationSchema = Yup.object().shape({
    description: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
});

export const addRCAActionRequestFormValidationSchema = Yup.object().shape({
    actionPlanName: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    actionPlanDescription: Yup.string()
        .trim()
        .min(1, "field_errors.required_field")
        .required("field_errors.required_field"),
    dueDate: Yup.date().required("field_errors.required_field"),
    assignedToId: Yup.number().required("field_errors.required_field"),
    priorityId: Yup.string().required("field_errors.required_field"),
    meetingTypeId: Yup.number(),
});

export const createRCAFormValidationSchema = Yup.object().shape({
    rcaDescription: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    dueDate: Yup.date().required("field_errors.required_field"),
    incidentDate: Yup.date().required("field_errors.required_field"),
    ownerId: Yup.number().required("field_errors.required_field"),
    statusId: Yup.string().required("field_errors.required_field"),
    meetingTypeId: Yup.number(),
    isAdmin: Yup.boolean(),
    millId: Yup.string().when("isAdmin", {
        is: true,
        then: Yup.string().required("field_errors.required_field"),
    }),
});

export const editRCAActionPlanRequestFormValidationSchema = Yup.object().shape({
    actionPlanName: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    actionPlanDescription: Yup.string()
        .trim()
        .min(1, "field_errors.required_field")
        .required("field_errors.required_field"),
});

export const addRCAActionPlanDetailFormValidationSchema = Yup.object().shape({
    statusId: Yup.number().required("field_errors.required_field"),
    meetingTypeId: Yup.number(),
    dueDate: Yup.date().required("field_errors.required_field"),
    assigneeId: Yup.number().required("field_errors.required_field"),
    priorityId: Yup.number().required("field_errors.required_field"),
});

export const addRCAIssueDetailFormValidationSchema = Yup.object().shape({
    statusId: Yup.number().required("field_errors.required_field"),
    meetingTypeId: Yup.number(),
    dueDate: Yup.date().required("field_errors.required_field"),
    ownerId: Yup.number().required("field_errors.required_field"),
    millId: Yup.number().required("field_errors.required_field"),
});

export const addRCALibraryFormValidationSchema = Yup.object().shape({
    statusId: Yup.number().required("field_errors.required_field"),
    meetingTypeId: Yup.number(),
    dueDate: Yup.date().required("field_errors.required_field"),
    ownerId: Yup.number().required("field_errors.required_field"),
    rcaTeamMembersId: Yup.array().of(Yup.number()).min(1, "field_errors.at_least_one_item_required"),
});

export const changePasswordFormValidationSchema = Yup.object().shape({
    currentPassword: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    newPassword: Yup.string()
        .matches(passwordValidatorRegex, "field_errors.password_validation")
        .required("field_errors.required_field"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "field_errors.passwords_must_match")
        .required("field_errors.required_field"),
});

export const addOrUpdateClusterSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
    production_lines: Yup.array().of(Yup.number()).min(1, "field_errors.at_least_one_item_required"),
    machine_centers: Yup.array()
        .of(Yup.number())
        .min(1, "field_errors.at_least_one_item_required")
        .max(12, "field_errors.max_hmis_per_cluster"),
});

export const forgotPasswordRequestFormValidationSchema = Yup.object().shape({
    email: Yup.string().matches(emailRegEx, "field_errors.invalid_email").required("field_errors.required_field"),
});

export const forgotPasswordResetFormValidationSchema = Yup.object().shape({
    newPassword: Yup.string()
        .matches(passwordValidatorRegex, "field_errors.password_validation")
        .required("field_errors.required_field"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "field_errors.passwords_must_match")
        .required("field_errors.required_field"),
});

export const changeHMICodeFormValidationSchema = Yup.object().shape({
    currentPassword: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
});

export const meetingTypesFormValidationSchema = Yup.object().shape({
    name: Yup.string().trim().min(1, "field_errors.required_field").required("field_errors.required_field"),
});
